import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AgreementStatus } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { useSite } from '../../lib/site';
import { useListState } from '../../lib/useList';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Badge, Button, Card, EmptyState, FilterBar, PageHeader, Pager, SelectField, SortHeader, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { AgreementPage, Agreement, BASE, ChainStatus, PAGE_SIZE, STATUSES, keys, statusLabel, statusTone } from './api';
import { AgreementDrawer } from './AgreementDrawer';

function ChainCard() {
  const q = useQuery({ queryKey: keys.chain, queryFn: () => api.get(`${BASE}/chain`, { schema: ChainStatus }) });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  const c = q.data;
  const label = c.verifiedOk === null ? t('agreementsUi.chain.never') : c.verifiedOk ? t('agreementsUi.chain.ok') : t('agreementsUi.chain.broken');
  return (
    <Card title={t('agreementsUi.chain.title')}>
      <div className="flex flex-wrap items-center gap-3">
        <Badge tone={c.verifiedOk === null ? 'neutral' : c.verifiedOk ? 'good' : 'bad'}>{label}</Badge>
        {c.verifiedAt ? <span className="text-sm">{t('agreementsUi.chain.checked', { when: formatDateTime(c.verifiedAt), count: c.lastSeq })}</span> : null}
        {c.timestampedAt ? <span className="text-sm">{t('agreementsUi.chain.timestamped', { when: formatDateTime(c.timestampedAt) })}</span> : null}
      </div>
      {c.verifiedOk === false ? (
        <p role="alert" className="mt-2 text-sm text-red-900">
          {t('agreementsUi.chain.brokenHelp')}
        </p>
      ) : null}
    </Card>
  );
}

export function AgreementsPage() {
  const { sites } = useSite();
  const qc = useQueryClient();
  const toast = useToast();
  const list = useListState({ sort: 'seq', order: 'desc' });
  const [siteId, setSiteId] = useState('');
  const [status, setStatus] = useState<'' | AgreementStatus>('');
  const [open, setOpen] = useState<string | null>(null);

  const query = { page: list.page, pageSize: PAGE_SIZE, search: list.search, sort: list.sort, order: list.order, siteId, status };
  const q = useQuery({
    queryKey: keys.list(query),
    queryFn: () => api.get(BASE, { query, schema: AgreementPage }),
    placeholderData: keepPreviousData,
  });
  const retry = useMutation({
    mutationFn: (a: Agreement) => api.post(`${BASE}/${a.id}/retry`, { schema: Agreement }),
    onSuccess: async () => {
      toast(t('agreementsUi.retry.done'));
      await qc.invalidateQueries({ queryKey: ['agreements'] });
    },
  });
  const sortProps = { sort: list.sort, order: list.order, onSort: list.toggleSort };

  return (
    <>
      <PageHeader title={t('agreementsUi.title')} description={t('agreementsUi.description')} />
      <div className="mb-4">
        <ChainCard />
      </div>
      <FilterBar columns="sm:grid-cols-3">
        <TextField label={t('common.search')} hint={t('agreementsUi.search.hint')} type="search" value={list.search} onChange={(e) => list.setSearch(e.target.value)} />
        <SelectField
          label={t('agreementsUi.filter.site')}
          value={siteId}
          onChange={(e) => {
            setSiteId(e.target.value);
            list.resetPage();
          }}
        >
          <option value="">{t('agreementsUi.filter.allSites')}</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </SelectField>
        <SelectField
          label={t('agreementsUi.filter.status')}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as '' | AgreementStatus);
            list.resetPage();
          }}
        >
          <option value="">{t('agreementsUi.filter.allStatuses')}</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {statusLabel(s)}
            </option>
          ))}
        </SelectField>
      </FilterBar>
      <ErrorLine error={retry.error} />
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <TableWrap caption={t('agreementsUi.table')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <SortHeader label={t('agreementsUi.col.seq')} field="seq" {...sortProps} />
                <SortHeader label={t('agreementsUi.col.person')} field="personName" {...sortProps} />
                <SortHeader label={t('agreementsUi.col.notice')} field="noticeTitle" {...sortProps} />
                <SortHeader label={t('agreementsUi.col.site')} field="siteName" {...sortProps} />
                <SortHeader label={t('agreementsUi.col.accepted')} field="acceptedAt" {...sortProps} />
                <Th>{t('agreementsUi.col.status')}</Th>
                <Th>{t('agreementsUi.col.actions')}</Th>
              </tr>
            </thead>
            <tbody>
              {q.data.items.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <EmptyState message={t('agreementsUi.empty')} />
                  </td>
                </tr>
              ) : null}
              {q.data.items.map((a) => {
                const person = a.personName ?? t('agreementsUi.redactedName');
                return (
                  <tr key={a.id} className="border-t border-slate-100 align-top hover:bg-slate-50">
                    <td className="px-4 py-3">{a.seq ?? ''}</td>
                    <td className="px-4 py-3">
                      <button type="button" className="font-medium text-brand hover:underline" aria-label={t('agreementsUi.open', { name: person })} onClick={() => setOpen(a.id)}>
                        {person}
                      </button>
                    </td>
                    <td className="px-4 py-3">{t('agreementsUi.noticeVersion', { title: a.noticeTitle, version: a.noticeVersion })}</td>
                    <td className="px-4 py-3">{a.siteName}</td>
                    <td className="px-4 py-3">{formatDateTime(a.acceptedAt)}</td>
                    <td className="px-4 py-3">
                      <Badge tone={statusTone(a.status)}>{statusLabel(a.status)}</Badge>
                      {a.errorKey && (a.status === 'failed' || a.status === 'pending') ? <div className="mt-1 max-w-xs text-xs text-slate-700">{t(a.errorKey)}</div> : null}
                    </td>
                    <td className="px-4 py-3">
                      {a.status === 'failed' ? (
                        <Button onClick={() => retry.mutate(a)} disabled={retry.isPending} aria-label={`${t('agreementsUi.retry')} ${person}`}>
                          {t('agreementsUi.retry')}
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TableWrap>
          <Pager page={list.page} pageSize={PAGE_SIZE} total={q.data.total} onPage={list.setPage} />
        </>
      )}
      {open ? <AgreementDrawer id={open} onClose={() => setOpen(null)} /> : null}
    </>
  );
}
